import { HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, throwError } from 'rxjs';

import { EnvironmentService, ErableHttpService } from '@ecm/consumer';
import { ContextService, PortfolioFlagsService } from '@ecm/portfolio';
import { ErrorPageService } from '@ecm/router';
import { KenobiParams, Status } from '@ecm/tracker';

import { Dashboard } from '../business/api/dashboard/dashboard';
import { DashboardV7 } from '../business/api/dashboardV7/dashboardV7';
import { KenobiTrackingCode } from '../business/model/tracking/kenobiTrackingCode-model';
import { TrackingService } from './tracking.service';

@Injectable({
    providedIn: 'root',
})
export class DashboardService {
    private readonly environmentService = inject(EnvironmentService);
    private readonly contextService = inject(ContextService);
    private readonly portfolioFlagsService = inject(PortfolioFlagsService);
    private readonly http = inject(ErableHttpService);
    private readonly errorService = inject(ErrorPageService);
    private readonly trackingService = inject(TrackingService);

    public getDashboard(
        kenobiTrackingCode: KenobiTrackingCode,
        pageId: string,
        requestParams?: HttpParams,
    ): Observable<DashboardV7 | Dashboard> {
        return this.requestInitDashboard(kenobiTrackingCode, pageId, requestParams);
    }

    private requestInitDashboard(
        kenobiTrackingCode: KenobiTrackingCode,
        pageId: string,
        requestParams?: HttpParams,
    ): Observable<any> {
        const env = this.environmentService.instant();

        if (env.API.DASHBOARD.DASHBOARD_URL === false) {
            this.errorService.showDefaultErrorPage();
            return throwError(() => 'dashboard is not activated');
        }

        // Use PortfolioFlagsService Signal directly instead of ContextService Observable
        const isSosh = this.portfolioFlagsService.isSosh();
        if (requestParams && isSosh) {
            requestParams = requestParams.set('requestedUniverse', 'sosh');
        }

        const queryStrings = this.paramtoQueryStrings(requestParams);
        return this.http
            .get(`${env.API.DASHBOARD.DASHBOARD_URL}?${queryStrings}`, {
                headers: this.getCustomHeaders(),
                feature: 'dashboard',
                pageId,
                functionality: kenobiTrackingCode['functionality'],
                partner: 'ZSH',
                service: 'dashboard',
            })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    this.trackingService.trackKenobi(
                        new KenobiParams(
                            kenobiTrackingCode['functionality'],
                            kenobiTrackingCode['rubric'],
                            Status.KOSI,
                        ),
                        pageId,
                    );
                    this.errorService.showDefaultErrorPage();
                    return of(error);
                }),
            );
    }

    /**
     * Returns custom HTTP Headers :
     * `Accept` header with `application/json;version=1` value
     * @returns {HttpHeaders}
     */
    private getCustomHeaders(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/json;version=7');
    }

    private paramtoQueryStrings(params: HttpParams): string {
        const parts = [];

        if (params) {
            for (const key of params.keys()) {
                const value = params.get(key);
                if (value) {
                    parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
                }
            }
        }
        return parts.join('&');
    }
}
